import {
  EmailAttachments,
  EmailDraftDetails,
  gmailSource,
} from "@email/EmailCards";
import {
  type EmailConversationMessage,
  type EmailDraft,
  type EmailDraftContext,
  type EmailMessage,
  isEditableDraft,
} from "@email/emailClient";
import { Button, FieldError } from "@scribenote/brian-design-system";
import { formatDateTime } from "@utils/format-date";

const preview = (body: string) => body.replace(/\s+/g, " ").trim();

const MessageRow = ({
  message,
  attachments,
}: {
  message: EmailConversationMessage;
  attachments: EmailMessage["attachments"];
}) => (
  <li>
    <details>
      <summary className="cursor-pointer list-none px-4 py-3 hover:bg-recess">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="font-medium text-ink">
              {message.sentByBrian
                ? "Brian"
                : message.mine
                  ? "Clinic"
                  : message.sender}
            </span>
            {message.sentByBrian && (
              <span className="rounded-full bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent">
                Sent
              </span>
            )}
          </div>
          {message.createdAt && (
            <span className="text-xs text-annotation">
              {formatDateTime(message.createdAt)}
            </span>
          )}
        </div>
        <p className="mt-1 line-clamp-1 text-sm text-annotation">
          {preview(message.body)}
        </p>
      </summary>
      <p className="max-h-64 overflow-y-auto whitespace-pre-wrap break-words border-t border-current/10 px-4 py-3 text-sm text-ink-body">
        {message.body}
      </p>
      <div className="px-4 pb-3">
        <EmailAttachments attachments={attachments} />
        <a
          href={gmailSource(message.providerId)}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-accent underline underline-offset-2"
        >
          Open source in Gmail
        </a>
      </div>
    </details>
  </li>
);

const DraftRow = ({
  draft,
  canApprove,
  onSelect,
}: {
  draft: EmailDraft;
  canApprove: boolean;
  onSelect?: () => void;
}) => (
  <li className="bg-accent/5">
    <details open={isEditableDraft(draft.status)}>
      <summary className="cursor-pointer list-none px-4 py-3 hover:bg-recess">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="font-medium text-ink">Brian</span>
            <span className="rounded-full bg-accent/10 px-2 py-0.5 text-xs font-medium uppercase text-accent">
              {draft.status === "draft" ? "Needs approval" : draft.status}
            </span>
          </div>
          <span className="text-xs text-annotation">
            {formatDateTime(draft.createdAt)}
          </span>
        </div>
        <p className="mt-1 line-clamp-1 text-sm text-annotation">
          {preview(draft.body)}
        </p>
      </summary>
      <div className="border-t border-current/10 px-4 pb-4">
        {onSelect && isEditableDraft(draft.status) && (
          <Button
            size="sm"
            variant="outline"
            className="mt-3"
            onClick={onSelect}
          >
            Open draft
          </Button>
        )}
        <EmailDraftDetails draft={draft} canApprove={canApprove} />
      </div>
    </details>
  </li>
);

export const EmailConversationContext = ({
  context,
  selectedMessage,
  drafts,
  loading,
  error,
  canApprove,
  selectedDraftUuid,
  onSelectDraft,
}: {
  context?: EmailDraftContext;
  selectedMessage?: EmailMessage;
  drafts: EmailDraft[];
  loading: boolean;
  error: unknown;
  canApprove: boolean;
  selectedDraftUuid?: string;
  onSelectDraft: (uuid: string) => void;
}) => {
  if (loading)
    return <p className="text-sm text-annotation">Loading conversation…</p>;
  if (error)
    return (
      <FieldError>
        Conversation context could not be loaded. Review it in Gmail before
        approving.
      </FieldError>
    );
  const messages = context?.messages ?? [];
  const messageIds = new Set(messages.map((message) => message.providerId));
  const visibleDrafts = drafts.filter(
    (draft) => !draft.providerId || !messageIds.has(draft.providerId),
  );
  if (!messages.length && !visibleDrafts.length) return null;
  const subject = visibleDrafts.at(-1)?.subject ?? messages.at(-1)?.subject;
  const count =
    (context?.totalMessages ?? messages.length) + visibleDrafts.length;
  return (
    <section className="overflow-hidden rounded-xl border border-current/10 bg-paper">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-current/10 px-4 py-3">
        <h4 className="font-display text-lg text-ink">
          {subject || "Email conversation"}
        </h4>
        <span className="text-xs text-annotation">
          {messages.length + visibleDrafts.length} of {count} emails
        </span>
      </div>
      {selectedMessage && selectedMessage.availability !== "available" && (
        <FieldError>
          Message content is {selectedMessage.availability}. Reconnect the
          mailbox or open the source in Gmail.
        </FieldError>
      )}
      <ol className="divide-y divide-current/10">
        {[
          ...messages.map((message) => ({
            key: message.providerId,
            date: message.createdAt ?? "",
            row: (
              <MessageRow
                key={message.providerId}
                message={message}
                attachments={
                  selectedMessage?.providerId === message.providerId
                    ? selectedMessage.attachments
                    : []
                }
              />
            ),
          })),
          ...visibleDrafts.map((draft) => ({
            key: draft.uuid,
            date: draft.createdAt,
            row: (
              <DraftRow
                key={draft.uuid}
                draft={draft}
                canApprove={canApprove}
                onSelect={
                  selectedDraftUuid !== draft.uuid
                    ? () => onSelectDraft(draft.uuid)
                    : undefined
                }
              />
            ),
          })),
        ]
          .sort((a, b) => (Date.parse(a.date) || 0) - (Date.parse(b.date) || 0))
          .map((item) => item.row)}
      </ol>
    </section>
  );
};
